import type { DifficultyLevel } from 'ms-components/PreviewProblem/types';
import DifficultyEasyIcon from 'ms-components/icons/DifficultyEasy';
import DifficultyHardIcon from 'ms-components/icons/DifficultyHard';
import DifficultyMediumIcon from 'ms-components/icons/DifficultyMedium';
import { BodyS } from 'ms-pages/Lantern/primitives/Typography';
import { colors } from 'ms-styles/colors';
import { assertUnreachable } from 'ms-utils/typescript-utils';

import { sharedStyles } from './sharedStyles';

const getDifficultyLabelProperties = (difficulty: DifficultyLevel) => {
  switch (difficulty) {
    case 'EASY':
      return {
        label: 'Easy',
        iconColor: colors.seaTeal,
        textColor: 'seaTeal',
        backgroundColor: colors.softAqua,
      } as const;
    case 'MEDIUM':
      return {
        label: 'Medium',
        iconColor: colors.bronzeOak,
        textColor: 'bronzeOak',
        backgroundColor: colors.goldenCream,
      } as const;
    case 'HARD':
      return {
        label: 'Hard',
        iconColor: colors.rustFire,
        textColor: 'rustFire',
        backgroundColor: colors.peachWhip,
      } as const;
    default: {
      assertUnreachable(difficulty, `Unknown DifficultyLevel ${difficulty}`);
    }
  }
};

type Props = {
  difficulty: DifficultyLevel;
  ratio?: number;
};

export const ProblemDifficultyLabel = ({ difficulty, ratio }: Props) => {
  const DifficultyIcon = (() => {
    switch (difficulty) {
      case 'EASY':
        return DifficultyEasyIcon;
      case 'MEDIUM':
        return DifficultyMediumIcon;
      case 'HARD':
        return DifficultyHardIcon;
      default: {
        assertUnreachable(difficulty, `Unknown DifficultyLevel ${difficulty}`);
      }
    }
  })();

  const { label, iconColor, textColor, backgroundColor } =
    getDifficultyLabelProperties(difficulty);

  const showRatio = ratio != null && ratio >= 0 && ratio <= 100;

  return (
    <div
      className={sharedStyles.labelWrapper}
      style={{
        backgroundColor,
        ...(showRatio && { width: `${ratio}%`, minWidth: '60px' }),
      }}
    >
      <DifficultyIcon color={iconColor} size={20} />
      <BodyS color={textColor}>{showRatio ? `${ratio}%` : label}</BodyS>
    </div>
  );
};
